/**
 * M3.1 HOTFIX · AvanzaConsentPanel
 *
 * Institutional Admin surface for recording parental "paper" consent
 * for B2B students imported via the M3.1 roster. Zero digital-consent
 * capability here (that is M3.3 scope).
 *
 * Consumes:
 *   GET  /api/admin/b2b/roster/students
 *   POST /api/admin/b2b/roster/students/{user_id}/consent/paper
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending: { text: "Pendiente",         cls: "bg-yellow-50 text-yellow-800" },
  paper:   { text: "Recibido en papel", cls: "bg-green-50 text-green-700"  },
  digital_granted: { text: "Digital",   cls: "bg-blue-50 text-blue-700"    },
  revoked: { text: "Revocado",          cls: "bg-red-50 text-red-700"      },
  rejected:{ text: "Rechazado",         cls: "bg-gray-100 text-gray-700"   },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || STATUS_LABEL.pending;
  return (
    <Badge
      className={s.cls}
      data-testid={`avanza-consent-status-${status || "pending"}`}
    >
      {s.text}
    </Badge>
  );
}

function RecordPaperDialog({ student, onRecorded }) {
  const [open, setOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    if (!ack) return;
    try {
      setSaving(true);
      const res = await axios.post(
        `${API}/admin/b2b/roster/students/${student.id}/consent/paper`,
        { ack_paper_consent: true },
        { withCredentials: true },
      );
      toast.success("Consentimiento en papel registrado.");
      setOpen(false);
      setAck(false);
      onRecorded?.(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === "cross_tenant_denied") {
        toast.error("No autorizado para modificar este estudiante.");
      } else if (detail === "ack_paper_consent_required") {
        toast.error("Debe confirmar explícitamente.");
      } else if (typeof detail === "string" && detail.startsWith("invalid_transition_from_")) {
        toast.error("El estado actual no permite pasar a 'recibido en papel'.");
      } else if (detail === "b2c_consent_not_managed_here") {
        toast.error("Este estudiante no pertenece a una institución B2B.");
      } else {
        toast.error("No se pudo registrar el consentimiento.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setAck(false); }}>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          data-testid={`avanza-consent-record-btn-${student.id}`}
        >
          Registrar consentimiento
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="avanza-consent-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Registrar consentimiento en papel</AlertDialogTitle>
          <AlertDialogDescription>
            Estudiante: <strong>{student.name || student.username}</strong>
            {student.grade ? ` · ${student.grade}` : ""}
            <br />
            Estado actual: <StatusBadge status={student.avanza_consent_status || "pending"} />
          </AlertDialogDescription>
        </AlertDialogHeader>

        <label className="flex items-start gap-3 py-3 text-sm text-gray-700">
          <Checkbox
            checked={ack}
            onCheckedChange={(v) => setAck(v === true)}
            data-testid="avanza-consent-ack-checkbox"
          />
          <span>
            Confirmo que la institución posee el consentimiento parental
            firmado en papel para este estudiante.
          </span>
        </label>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="avanza-consent-cancel-btn">
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); handleConfirm(); }}
            disabled={!ack || saving}
            data-testid="avanza-consent-confirm-btn"
          >
            {saving ? "Guardando..." : "Confirmar y guardar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function AvanzaConsentPanel() {
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/b2b/roster/students`, {
        withCredentials: true,
      });
      setStudents(res.data?.students || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "cross_tenant_denied" || err?.response?.status === 403) {
        toast.error("No autorizado.");
      } else {
        toast.error("No se pudo cargar la lista de estudiantes.");
      }
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onRecorded = (payload) => {
    setStudents((prev) => prev.map((s) => (
      s.id === payload.user_id
        ? { ...s,
            avanza_consent_status: payload.avanza_consent_status,
            avanza_consent_meta: payload.avanza_consent_meta }
        : s
    )));
  };

  return (
    <div className="space-y-4" data-testid="avanza-consent-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <p className="text-sm text-gray-600">
            Consentimiento Buddy Avanza (estudiantes B2B importados por roster)
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="avanza-consent-refresh-btn"
        >
          {loading ? "Actualizando..." : "Actualizar"}
        </Button>
      </div>

      <div className="rounded-xl border border-gray-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Estudiante</TableHead>
              <TableHead>Usuario</TableHead>
              <TableHead>Grado</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Registrado por</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Acción</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="avanza-consent-tbody">
            {students.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-gray-400 py-6">
                  No hay estudiantes importados por roster todavía.
                </TableCell>
              </TableRow>
            )}
            {students.map((s) => {
              const meta = s.avanza_consent_meta || {};
              const status = s.avanza_consent_status || "pending";
              const canRecordPaper = status === "pending" || status === "paper";
              return (
                <TableRow key={s.id} data-testid={`avanza-consent-row-${s.id}`}>
                  <TableCell className="font-medium">{s.name || "—"}</TableCell>
                  <TableCell>{s.username || "—"}</TableCell>
                  <TableCell>{s.grade || "—"}</TableCell>
                  <TableCell><StatusBadge status={status} /></TableCell>
                  <TableCell className="text-xs text-gray-500">
                    {meta.recorded_by ? meta.recorded_by.slice(0, 8) + "…" : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">
                    {meta.recorded_at ? meta.recorded_at.slice(0, 10) : "—"}
                  </TableCell>
                  <TableCell>
                    {canRecordPaper ? (
                      <RecordPaperDialog student={s} onRecorded={onRecorded} />
                    ) : (
                      <span className="text-xs text-gray-400">No editable</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
